import { Button, Flex, Select, CopyButton } from '@mantine/core';
import Cookies from 'js-cookie';
import { COOKIES_KEYS } from './../../app-constants/constants';
import { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { HeaderWrapper } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { changLang, getLangState } from '../../store/lang.slice';
import { getTenant } from '../../store/tenant.slice';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { useLocation, useParams } from 'react-router-dom';
import { ImLink } from 'react-icons/im';
import useOutsideClick from '../../hooks/useOutsideClick';
import { theme } from '@jadwalh/ui';
import { getClientData } from '../../store/clientData.slice';
import { getAuthToken } from '../../utils/cookie-helper';
import { getEventData } from '../../store/eventData.slice';

export default function Header({ showHeader }: { showHeader?: boolean }) {
  const { formatMessage } = useIntl();
  const [showActions, setShowActions] = useState(false);
  const [selectValue, setSelectValue] = useState('');
  const [isClient, setIsClient] = useState(false);
  const [resetKey, setResetKey] = useState(Date.now());
  const ref = useOutsideClick(() => setShowActions(false));
  const dispatch = useDispatch();
  const location = useLocation();
  const selectRef = useRef<HTMLInputElement>(null);
  const { event,client } = useParams();

  /* ------------------------------- Global State ------------------------------ */
  const lang = useSelector(getLangState);
  const tenant = useSelector(getTenant);
  const [token] = useState<string | undefined>(getAuthToken() || undefined);

  const getLangSelected = Cookies.get(COOKIES_KEYS.LanguageAdded);
  const clientData = useSelector(getClientData);
  const eventData = useSelector(getEventData);

  useEffect(() => {
    if (!getLangSelected) return;
    dispatch(changLang({ lang: getLangSelected as 'ar' | 'en' }));
  }, [getLangSelected, dispatch]);

  // Handle client-side hydration
  useEffect(() => {
    setIsClient(true);
    // Force reset on client-side hydration
    setResetKey(Date.now());
  }, []);

  // Force complete component reset on any navigation
  useEffect(() => {
    if (!isClient) return; // Only run on client side

    // Force a complete reset by incrementing the key and clearing value
    setResetKey(Date.now());
    setSelectValue('');
  }, [location.pathname, isClient]);

  // Reset on browser back/forward navigation
  useEffect(() => {
    if (!isClient) return; // Only run on client side

    const handlePopState = () => {
      setResetKey(Date.now());
      setSelectValue('');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isClient]);

  // Force reset on component mount
  useEffect(() => {
    setResetKey(Date.now());
    setSelectValue('');
  }, []);

  return (
    window.self === window.top &&
    showHeader && eventData && eventData.type_value !== 4 && (event || client) && (
    (tenant.settings.reservation_identity.logo ||
      (token && clientData?.is_member_in_event && (event || client))) && (
      <nav className="headerBar">
        <HeaderWrapper>
          <Flex w={'100%'} justify={'center'} align={'center'}>
            <div className="HeaderSection">
              <Flex justify={'space-between'} align={'center'} w={'100%'} m={0}>
                {clientData?.is_member_in_event && isClient && event && (
                  <Select
                    className="small-screen select-action"
                    value={selectValue}
                    lang={lang}
                    data={[
                      {
                        value: '',
                        label: formatMessage({ id: 'Jadwalh' }),
                        disabled: true,
                        hidden: true,
                      },
                      {
                        value: 'home',
                        label: formatMessage({ id: 'home' }),
                      },
                      ...(clientData.has_permission
                        ? [
                            {
                              value: 'editEvent',
                              label: formatMessage({ id: 'editEvent' }),
                            },
                          ]
                        : []),
                    ]}
                    onChange={(value) => {
                      setSelectValue(value || '');             
                      if (value === 'home') {
                        if (window.location.port === '') {
                          const hostParts = window.location.host.split('.');
                          const baseUrl =
                            hostParts.length > 2 && hostParts[0] !== 'www'
                              ? `https://app-${hostParts[0]}.jadwalh.com`
                              : 'https://app.jadwalh.com';
                          token
                            ? (window.location.href = `${baseUrl}/appointments`)
                            : (window.location.href = `${baseUrl}/login`);
                        } else {
                          window.location.href = 'http://localhost:3000/login';
                        }
                      } else if (value === 'editEvent') {
                        window.location.href = clientData.event_edit_link;
                      }
                    }}
                    withinPortal={true}
                    classNames={{ dropdown: 'status-options-dropdown' }}
                    icon={<MdKeyboardArrowDown />}
                    rightSection={''}
                    styles={() => ({
                      wrapper: {
                        width: 100,
                        height: 30,
                        maxWidth: 150,
                        textOverflow: 'ellipsis',
                      },
                      item: {
                        wordBreak: 'normal',
                        textAlign:
                          localStorage.getItem('lang') === 'ar'
                            ? 'right'
                            : 'left',
                        fontSize: '13px',
                        fontFamily: theme.fonts.Regular,
                      },
                    })}
                  />
                )}
                {tenant && (
                  <div className="containerLogo" lang={lang}>
                    {tenant.settings.reservation_identity.logo ? (
                      <img
                        alt={tenant.name}
                        src={tenant.settings.reservation_identity.logo}
                      />
                    ) : (
                      <Flex
                        align={'center'}
                        justify={'center'}
                        h={'59px'}
                      ></Flex>
                    )}
                  </div>
                )}
                <div>
                  {' '}
                  {clientData?.is_member_in_event && isClient && (event || client) ? (
                    <Flex
                      align={'center'}
                      gap={8}
                      className="buttons_wrapper"
                      lang={lang}
                    >
                      <Select
                        className="select-action"
                        defaultValue={''}
                        data={[
                          {
                            value: '',
                            label: formatMessage({ id: 'Jadwalh' }),
                            disabled: true,
                            hidden: true,
                          },
                          {
                            value: 'home',
                            label: formatMessage({ id: 'home' }),
                          },
                          ...(clientData.has_permission
                            ? [
                                {
                                  value: 'editEvent',
                                  label: formatMessage({ id: 'editEvent' }),
                                },
                              ]
                            : []),
                        ]}
                        onChange={(value) => {
                          if (value === 'home') {
                            if (window.location.port === '') {
                              const hostParts = window.location.host.split('.');
                              const baseUrl =
                                hostParts.length > 2 && hostParts[0] !== 'www'
                                  ? `https://app-${hostParts[0]}.jadwalh.com`
                                  : 'https://app.jadwalh.com';
                              token
                                ? (window.location.href = `${baseUrl}/appointments`)
                                : (window.location.href = `${baseUrl}/login`);
                            } else {
                              window.location.href =
                                'http://localhost:3000/login';
                            }
                          } else if (value === 'editEvent') {
                            window.location.href = clientData.event_edit_link;
                          }
                        }}
                        classNames={{ dropdown: 'status-options-dropdown' }}
                        icon={<MdKeyboardArrowDown />}
                        rightSection={''}
                        styles={() => ({
                          wrapper: {
                            width: 120,
                            height: 30,
                            maxWidth: 120,
                            textOverflow: 'ellipsis',
                          },
                          item: {
                            wordBreak: 'normal',
                            textAlign:
                              localStorage.getItem('lang') === 'ar'
                                ? 'right'
                                : 'left',
                            fontSize: '13px',
                            fontFamily: theme.fonts.Regular,
                          },
                        })}
                      />
                      <CopyButton value={window.location.href} timeout={2000}>
                        {({ copied, copy }) => (
                          <Button
                            className={copied ? 'copied' : ''}
                            onClick={copy}
                          >
                            {copied
                              ? formatMessage({ id: 'copied' })
                              : formatMessage({ id: 'copyLink' })}
                            <ImLink />
                          </Button>
                        )}
                      </CopyButton>
                    </Flex>
                  ) : (
                    <div />
                  )}
                </div>
              </Flex>
            </div>
          </Flex>
        </HeaderWrapper>
      </nav>
    )
    )
  );
}
